/**@jsxImportSource theme-ui */
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { DismissButton, OverlayContainer, useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';
import { AnimatePresence } from 'framer-motion';
import React, { RefObject } from 'react';
import Container, { ContainerProps } from '../Container';
import { AllCaroteneColors } from '../utils/types';

export interface OverlayProps {
    children: React.ReactNode | React.ReactNodeArray;
    open: boolean;
    background: AllCaroteneColors;
}

export function Overlay({ open, children, background }: OverlayProps): JSX.Element {
    return (
        <AnimatePresence>
            {open ? (
                <OverlayContainer>
                    <div
                        sx={{
                            position: 'fixed',
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            top: 0,
                            left: 0,
                            zIndex: 999,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: background,
                        }}
                    >
                        {children}
                    </div>
                </OverlayContainer>
            ) : null}
        </AnimatePresence>
    );
}

interface OverlayContentProps extends ContainerProps, OverlayControlProps {
    /**
     * Will come back to fix this type eventually, but the requirement is a component that accepts 'onClick'
     * Example: `<OverlayContent closeButton={Button}/>`
     */
    closeButton: any;

    /**
     * Leaving this type loose for now, but the intent is to pass a framer-motion component in.
     */
    animationLayer?: any;
}

export type OverlayControlProps = {
    open: boolean;
    onClose: () => unknown;
    isDismissable?: boolean;
};

export const OverlayContent = React.forwardRef(
    (
        {
            children,
            open,
            onClose,
            isDismissable,
            closeButton: CloseButton,
            sx,
            animationLayer: AnimationLayer,
            ...otherProps
        }: OverlayContentProps,
        ref: RefObject<HTMLDivElement>
    ) => {
        const { overlayProps } = useOverlay(
            {
                onClose,
                isOpen: open,
                isDismissable,
                isKeyboardDismissDisabled: !isDismissable,
            },
            ref
        );
        usePreventScroll();
        const { modalProps } = useModal();

        const { dialogProps } = useDialog({}, ref);
        return (
            <AnimationLayer>
                <FocusScope contain restoreFocus>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
                    {/* @ts-ignore */}
                    <div ref={ref} {...mergeProps(overlayProps, dialogProps, modalProps)}>
                        <Container shadow="modal" sx={sx} {...otherProps}>
                            {CloseButton && <CloseButton onClick={onClose} />}
                            {children}
                            <DismissButton onDismiss={onClose} />
                        </Container>
                    </div>
                </FocusScope>
            </AnimationLayer>
        );
    }
);

OverlayContent.displayName = 'OverlayContent';

OverlayContent.defaultProps = {
    animationLayer: React.Fragment,
};
